import { useNavigate } from 'react-router';
import { ArrowLeft, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PageHeaderProps } from '@/lib/types';

export function PageHeader({
  title,
  subtitle,
  icon,
  iconBgClass = 'bg-blue-100',
  backTo,
  backBehavior = 'history',
  actions,
  userName,
  userRole,
  onLogout,
}: PageHeaderProps) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (!backTo) return;
    if (backBehavior === 'path') {
      navigate(backTo);
      return;
    }
    // React Router tracks stack position in history.state.idx; only POP when we can.
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === 'number' && idx > 0) {
      navigate(-1);
    } else {
      navigate(backTo);
    }
  };

  return (
    <header className="bg-white border-b sticky top-0 z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {backTo && (
              <Button variant="ghost" size="sm" onClick={handleBack}>
                <ArrowLeft className="size-4" />
              </Button>
            )}
            {icon && (
              <div className={`hidden sm:flex p-2 ${iconBgClass} rounded-lg shrink-0`}>
                {icon}
              </div>
            )}
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">{title}</h1>
              {subtitle && <p className="text-xs sm:text-sm text-gray-500 truncate">{subtitle}</p>}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            {actions}
            {userName && (
              <div className="hidden md:block text-right">
                <p className="text-sm font-medium text-gray-900">{userName}</p>
                <p className="text-xs text-gray-500 capitalize">{userRole}</p>
              </div>
            )}
            {onLogout && (
              <Button variant="outline" size="sm" onClick={onLogout}>
                <LogOut className="size-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
